import React from 'react';
import { PiFlag, PiGavel, PiHandPalm } from 'react-icons/pi';
import { StatusCard } from './VesselStatusCard';

// ============================================================================
// Types
// ============================================================================

export interface Inspection {
  inspection_date: string;
  inspection_type: string;
  inspection_port: string;
  inspection_country: string;
  deficiency_count: number;
  deficiency_ground_for_detention_count: number;
}

export interface Ban {
  ban_begin_date: string;
  ban_country_of_inspection: string;
  ban_end_date: string | null;
  ban_reason: string;
  related_psc: string;
  ban_status: string;
}

export interface Sanction {
  sanction_by: string;
  program: string;
  reason: string;
  sanction_type: string;
  sanction_begin_date: string;
  sanction_end_date: string | null;
}

export interface AdvancedIntegrity {
  inspections?: Inspection[];
  bans?: Ban[];
  sanctions?: Sanction[];
  flagPerformanceTokyo?: string;
  flagPerformanceParis?: string;
}

// ============================================================================
// Integrity Status Cards
// ============================================================================

interface PopupIntegrityStatusCardsProps {
  advancedIntegrity?: AdvancedIntegrity;
  hasPermission: boolean;
}

const getFlagStatus = (
  value?: string,
): { value: string; severity: 'low' | 'medium' | 'high' } => {
  if (!value) return { value: 'Unknown', severity: 'low' };
  const lower = value.toLowerCase();
  if (lower === 'black') return { value, severity: 'high' };
  if (lower === 'grey') return { value, severity: 'medium' };
  return { value, severity: 'low' };
};

export const PopupIntegrityStatusCards: React.FC<
  PopupIntegrityStatusCardsProps
> = ({ advancedIntegrity, hasPermission }) => {
  if (!hasPermission) {
    return null;
  }

  const tokyo = getFlagStatus(advancedIntegrity?.flagPerformanceTokyo);
  const paris = getFlagStatus(advancedIntegrity?.flagPerformanceParis);

  const sanctionCount = advancedIntegrity?.sanctions?.length || 0;
  const banCount = advancedIntegrity?.bans?.length || 0;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatusCard
        title="Flag Status Tokyo"
        icon={<PiFlag />}
        value={tokyo.value}
        severity={tokyo.severity}
      />
      <StatusCard
        title="Flag Status Paris"
        icon={<PiFlag />}
        value={paris.value}
        severity={paris.severity}
      />
      <StatusCard
        title="Sanction"
        icon={<PiGavel />}
        value={sanctionCount > 0 ? 'Sanctioned' : 'Not sanctioned'}
        description={
          sanctionCount > 0
            ? `Has ${sanctionCount} sanction${sanctionCount > 1 ? 's' : ''}`
            : undefined
        }
        severity={sanctionCount > 0 ? 'high' : 'low'}
      />
      <StatusCard
        title="Ban"
        icon={<PiHandPalm />}
        value={banCount > 0 ? 'Banned' : 'Not banned'}
        description={
          banCount > 0
            ? `Has ${banCount} ban${banCount > 1 ? 's' : ''}`
            : undefined
        }
        severity={banCount > 0 ? 'high' : 'low'}
      />
    </div>
  );
};
